import React, { useState } from 'react';
import { buildLanguageMap } from '../utils/languageConfig';
import './TranslationPreview.css';

const TranslationPreview = ({
  translatedData,
  sourceLanguage = 'en',
  supportedLanguages,
  onApprove,
  onCancel,
  onEdit,
  loading = false
}) => {
  const [currentLanguage, setCurrentLanguage] = useState(sourceLanguage);
  const [showAllFields, setShowAllFields] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [localTranslatedData, setLocalTranslatedData] = useState(translatedData);
  // Track which languages the admin actually hand-edited in this preview.
  // Only these are sent as overwriteLanguages so the backend B2 guard permits
  // replacing an already-populated (reviewed) field. MT-generated languages are
  // NOT added here — a plain Translate All therefore sends overwriteLanguages:[].
  const [editedLangs, setEditedLangs] = useState(() => new Set());

  // Build language map from supportedLanguages prop (ISO codes), or fall back to translatedData keys
  const languages = supportedLanguages
    ? (typeof supportedLanguages === 'object' && !Array.isArray(supportedLanguages)
        ? supportedLanguages
        : buildLanguageMap(supportedLanguages))
    : buildLanguageMap(Object.keys(translatedData || {}));

  // Initialize localTranslatedData from props if not already set
  React.useEffect(() => {
    if (translatedData) {
      setLocalTranslatedData(translatedData);
    }
  }, [translatedData]);

  const availableLanguages = Object.keys(localTranslatedData || {});
  const currentData = localTranslatedData?.[currentLanguage] || {};

  // Safety check - if no languages available, show error
  if (!translatedData || availableLanguages.length === 0) {
    return (
      <div className="translation-preview">
        <div className="no-data">
          <h3>⚠️ No Translation Data</h3>
          <p>Translation data is not available. Please try translating again.</p>
          <button className="btn-cancel" onClick={onCancel}>
            Go Back
          </button>
        </div>
      </div>
    );
  }

  // Handle inline editing
  const startEditing = (fieldKey, currentValue) => {
    setEditingField(`${currentLanguage}-${fieldKey}`);
    setEditValue(currentValue || '');
  };

  const saveEdit = () => {
    const [lang, fieldKey] = editingField.split('-');
    const updatedData = {
      ...localTranslatedData,
      [lang]: {
        ...localTranslatedData[lang],
        [fieldKey]: editValue
      }
    };
    setLocalTranslatedData(updatedData);
    // Mark this language as hand-edited → it will be opted-in for overwrite on save.
    setEditedLangs(prev => {
      const next = new Set(prev);
      next.add(lang);
      return next;
    });
    setEditingField(null);
    setEditValue('');
  };

  const cancelEdit = () => {
    setEditingField(null);
    setEditValue('');
  };



  const getNextLanguage = () => {
    const currentIndex = availableLanguages.indexOf(currentLanguage);
    const nextIndex = (currentIndex + 1) % availableLanguages.length;
    return availableLanguages[nextIndex];
  };

  const getPrevLanguage = () => {
    const currentIndex = availableLanguages.indexOf(currentLanguage);
    const prevIndex = currentIndex === 0 ? availableLanguages.length - 1 : currentIndex - 1;
    return availableLanguages[prevIndex];
  };

  // Translatable fields — these are editable per-language
  const TRANSLATABLE_FIELDS = new Set([
    'description', 'long_description', 'tips', 'history',
    'features', 'facilities', 'accessibility'
  ]);

  const isReadOnlyField = (fieldKey) => {
    // English/source tab is entirely read-only (edits don't persist — use Quick Save for English)
    if (currentLanguage === sourceLanguage) return true;
    // Non-translatable fields are read-only in all tabs
    return !TRANSLATABLE_FIELDS.has(fieldKey);
  };

  const renderField = (label, value, isArray = false, fieldKey = '') => {
    const readOnly = isReadOnlyField(fieldKey);

    // CRITICAL: Convert objects to strings to prevent React error "Objects are not valid as a React child"
    let displayValue = value || '';

    // Handle objects (like operating_hours JSON object)
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      try {
        displayValue = JSON.stringify(value, null, 2);
      } catch (e) {
        displayValue = String(value);
      }
    } else if (value !== null && value !== undefined) {
      displayValue = String(value);
    } else {
      displayValue = '';
    }

    const isEditing = editingField === `${currentLanguage}-${fieldKey}`;

    if (isArray && Array.isArray(value)) {
      return (
        <div className="translation-field">
          <label className="field-label">
            {label}:
            {readOnly && <span className="read-only-hint" style={{fontSize:'11px',color:'#888',marginLeft:'6px'}}>(same across languages)</span>}
          </label>
          <div className="field-value array-value">
            {value.length > 0 ? (
              value.map((item, index) => (
                <span key={index} className="array-item">{String(item)}</span>
              ))
            ) : (
              <span className="empty-field">No content - click to add</span>
            )}
          </div>
        </div>
      );
    }

    return (
      <div className="translation-field">
        <label className="field-label">
          {label}:
          {readOnly && <span className="read-only-hint" style={{fontSize:'11px',color:'#888',marginLeft:'6px'}}>(same across languages)</span>}
        </label>
        {isEditing && !readOnly ? (
          <div className="field-edit-container">
            <textarea
              className="field-edit-input"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              rows={fieldKey === 'operating_hours' ? 8 : 3}
              autoFocus
            />
            <div className="field-edit-actions">
              <button className="btn-save-edit" onClick={saveEdit}>
                ✅ Save
              </button>
              <button className="btn-cancel-edit" onClick={cancelEdit}>
                ❌ Cancel
              </button>
            </div>
          </div>
        ) : (
          <div
            className={`field-value${readOnly ? ' read-only' : ''}`}
            onClick={() => !readOnly && startEditing(fieldKey, displayValue)}
            style={readOnly ? { cursor: 'default', opacity: 0.85 } : undefined}
          >
            {displayValue ? (
              <pre className={fieldKey === 'operating_hours' ? 'operating-hours-display' : ''}>
                {displayValue}
              </pre>
            ) : (
              <span className="empty-field">No content - click to add</span>
            )}
            {!readOnly && <span className="edit-hint">✏️ Click to edit</span>}
          </div>
        )}
      </div>
    );
  };

  const renderMainFields = () => (
    <>
      {renderField('Name', currentData.name, false, 'name')}
      {renderField('Category', currentData.category, false, 'category')}
      {renderField('Subcategory', currentData.subcategory, false, 'subcategory')}
      {/* Display Category row removed 2026-08-04 alongside the form control — the field is dead (browse + chat derive from `category`). */}
      {renderField('Description', currentData.description, false, 'description')}
      {renderField('Operating Hours', currentData.operating_hours, false, 'operating_hours')}
      {renderPricingSection()}
    </>
  );

  const renderPricingSection = () => {
    return (
      <div className="field-group">
        <h4 className="section-title">💰 Pricing Information</h4>
        {renderField('Entrance Fees', currentData.pricing_type, false, 'pricing_type')}
      </div>
    );
  };

  const renderAllFields = () => (
    <>
      {renderMainFields()}
      {renderField('Tips', currentData.tips, false, 'tips')}
      {renderField('History', currentData.history, false, 'history')}
      {renderField('Contact Person', currentData.pic_contact, false, 'pic_contact')}
      {renderField('Features', currentData.features, true, 'features')}
      {renderField('Facilities', currentData.facilities, true, 'facilities')}
      {renderField('Accessibility', currentData.accessibility, true, 'accessibility')}
    </>
  );

  return (
    <div className="translation-preview">
      <div className="translation-header">
        <h3>🌐 Translation Preview</h3>
        <div className="language-info">
          <span className="source-language">
            Source: {languages[sourceLanguage]?.flag} {languages[sourceLanguage]?.name}
          </span>
          <span className="total-languages">
            {availableLanguages.length} languages ready
          </span>
        </div>
      </div>

      <div className="language-navigation">
        <button
          className="nav-button prev"
          onClick={() => setCurrentLanguage(getPrevLanguage())}
          disabled={availableLanguages.length <= 1}
        >
          ← Previous
        </button>

        <div className="current-language">
          <span className="language-flag">{languages[currentLanguage]?.flag}</span>
          <span className="language-name">{languages[currentLanguage]?.name}</span>
          <span className="language-count">
            ({availableLanguages.indexOf(currentLanguage) + 1} of {availableLanguages.length})
          </span>
        </div>

        <button
          className="nav-button next"
          onClick={() => setCurrentLanguage(getNextLanguage())}
          disabled={availableLanguages.length <= 1}
        >
          Next →
        </button>
      </div>

      <div className="language-tabs">
        {availableLanguages.map(lang => (
          <button
            key={lang}
            className={`language-tab ${currentLanguage === lang ? 'active' : ''}`}
            onClick={() => setCurrentLanguage(lang)}
          >
            {languages[lang]?.flag} {languages[lang]?.name}
          </button>
        ))}
      </div>

      <div
        className={`translation-content ${languages[currentLanguage]?.direction}`}
        dir={languages[currentLanguage]?.direction}
      >
        <div className="content-header">
          <h4>
            {languages[currentLanguage]?.flag} {languages[currentLanguage]?.name} Translation
          </h4>
          <button
            className="toggle-fields"
            onClick={() => setShowAllFields(!showAllFields)}
          >
            {showAllFields ? 'Show Less' : 'Show All Fields'}
          </button>
        </div>

        <div className="fields-container">
          {showAllFields ? renderAllFields() : renderMainFields()}
        </div>

        {currentLanguage === sourceLanguage && (
          <div className="source-indicator">
            ⭐ This is the source language (read-only). Use "Save Image & Data Only" to edit English fields.
          </div>
        )}
      </div>

      <div className="preview-actions">
        <div className="action-buttons">
          <button
            className="btn-cancel"
            onClick={onCancel}
            disabled={loading}
          >
            Cancel
          </button>

          <button
            className="btn-approve"
            onClick={() => onApprove(localTranslatedData, [...editedLangs])}
            disabled={loading}
          >
            {loading ? (
              <>
                <span className="spinner"></span>
                Saving...
              </>
            ) : editedLangs.size > 0 ? (
              <>
                ✅ Save Edits ({[...editedLangs].join(', ')})
              </>
            ) : (
              <>
                ✅ Approve & Save All Translations
              </>
            )}
          </button>
        </div>

        <div className="approval-note">
          <p>Approving will save the attraction in all {availableLanguages.length} languages to the database.</p>
        </div>
      </div>
    </div>
  );
};

export default TranslationPreview;
